const destinations = [
  {
    name: "Italy",
    context: "Florence art history, Venice Biennale, Rome galleries, Ringling College study abroad programs",
    urgency: "Study abroad deadlines are immovable — often 4–6 weeks before departure",
    icon: "ri-map-pin-2-line",
    flag: "IT",
  },
  {
    name: "France",
    context: "Paris gallery weeks, Provence art retreats, Lyon design festivals",
    urgency: "Gallery openings and festival invitations often come 2–3 weeks out",
    icon: "ri-map-pin-2-line",
    flag: "FR",
  },
  {
    name: "United Kingdom",
    context: "London theatre seasons, Edinburgh Festival Fringe, Oxford cultural tours",
    urgency: "Performing arts tours often confirm 3–4 weeks before opening night",
    icon: "ri-map-pin-2-line",
    flag: "GB",
  },
  {
    name: "Spain",
    context: "Barcelona architecture tours, Madrid art museums, Seville flamenco festivals",
    urgency: "Architecture and design tours book 4–8 weeks out, but last-minute openings happen",
    icon: "ri-map-pin-2-line",
    flag: "ES",
  },
  {
    name: "Caribbean Islands",
    context: "Cruises from Tampa, winter art retreats, gallery shows in Puerto Rico",
    urgency: "Cruise flash sales create 30–60 day booking windows with passport surprises",
    icon: "ri-map-pin-2-line",
    flag: "BS",
  },
  {
    name: "Canada",
    context: "Toronto and Montreal arts festivals, summer cultural exchange programs",
    urgency: "Summer festival season creates predictable June–July passport rushes",
    icon: "ri-map-pin-2-line",
    flag: "CA",
  },
];

const commonSituations = [
  {
    stat: "68%",
    label: "of Sarasota expedited requests are renewals discovered within 2 weeks of travel",
  },
  {
    stat: "4–8 Weeks",
    label: "average notice Sarasota arts travelers have before a planned international trip",
  },
  {
    stat: "Oct–Mar",
    label: "peak season when arts season and snowbird arrivals converge on passport demand",
  },
  {
    stat: "72 Hours",
    label: "shortest performing arts tour departure we have accommodated for a Sarasota client",
  },
];

export default function SarasotaDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Sarasota Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            These are the destinations and travel patterns we see most from Sarasota and Cultural Coast clients. Understanding local travel behavior is part of why our document review catches issues before they derail your arts tour or study abroad program.
          </p>
        </div>

        {/* Stat block row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {commonSituations.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Destinations grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
                </div>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">
                {d.context}
              </p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">
                  {d.urgency}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal linking block */}
        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Florida Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across the Gulf Coast and Central Florida with the same expedited passport processing and document review expertise. Looking for service in another community?
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/tampa" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Tampa <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/cities/orlando" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Orlando <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/cities/naples" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Naples <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}